import * as React from 'react';
import styles from './index.less';
import FSelect2 from '@/components/FSelect2';
import FComponentsLib from '@freelog/components-lib';
import { FUtil, FI18n, FServiceAPI } from '@freelog/tools-lib';
import { Space } from 'antd';
import fMessage from '@/components/fMessage';
import FIcons from '@/components/FIcons';
import { Radio, Checkbox, Input } from 'antd';
import { FResourceTypeInputBlock } from '@/components/FResourceTypeInputBlock';
import * as AHooks from 'ahooks';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';

interface AutoIncludedSettingProps {
  nodeName: string;
  exhibitID: string;
}

interface AutoIncludedSettingStates {
  automatic: boolean;
  conditionType: 'every' | 'some';
  conditions: {
    key: 'presentableTitle' | 'resourceTypeCode' | 'authIdentity';
    limitOperatorType:
      | 'INCLUDES'
      | 'NOT_INCLUDES'
      | 'STARTS_WITH'
      | 'ENDS_WITH'
      | 'EQUAL'
      | 'NOT_EQUAL';
    value: string;
    valueError: string;
  }[];
}

const defaultSetting: AutoIncludedSettingStates = {
  automatic: false,
  conditionType: 'every',
  conditions: [
    {
      key: 'presentableTitle',
      limitOperatorType: 'INCLUDES',
      value: '',
      valueError: '',
    },
  ],
};

export function AutoIncludedSetting({ nodeName, exhibitID }: AutoIncludedSettingProps) {
  const [$automatic, set$automatic, get$automatic] = FUtil.Hook.useGetState<
    AutoIncludedSettingStates['automatic']
  >(defaultSetting.automatic);
  const [$conditionType, set$conditionType, get$conditionType] = FUtil.Hook.useGetState<
    AutoIncludedSettingStates['conditionType']
  >(defaultSetting.conditionType);
  const [$conditions, set$conditions, get$conditions] = FUtil.Hook.useGetState<
    AutoIncludedSettingStates['conditions']
  >(defaultSetting.conditions);
  const [$isEdit, set$isEdit, get$isEdit] = FUtil.Hook.useGetState<boolean>(false);

  AHooks.useMount(async () => {
    initDate();
  });

  async function initDate() {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        status: 0 | 1;
        conditionType: 1 | 2;
        filterConditions: {
          key: 'presentableTitle' | 'resourceTypeCode' | 'authIdentity';
          limitOperatorType:
            | 'INCLUDES'
            | 'NOT_INCLUDES'
            | 'STARTS_WITH'
            | 'ENDS_WITH'
            | 'EQUAL'
            | 'NOT_EQUAL';
          value: string;
        }[];
      } | null;
    } = await FServiceAPI.Exhibit.getItemsAutoCollectRule({
      presentableId: exhibitID,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    if (data) {
      set$conditionType(data.conditionType === 1 ? 'every' : 'some');
      set$automatic(data.status === 1 || false);
      set$conditions(
        data.filterConditions.map((fc) => {
          return {
            ...fc,
            value: fc.value,
            valueError: '',
          };
        }) || [],
      );
    } else {
      set$automatic(defaultSetting.automatic);
      set$conditionType(defaultSetting.conditionType);
      set$conditions(defaultSetting.conditions);
    }
  }

  return (
    <div className={styles.block}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <FComponentsLib.FContentText
          text={
            // 自动收入
            FI18n.i18nNext.t('edit_exhibitcollection_autoadd_title')
          }
          type={'highlight'}
        />

        <Space size={10}>
          {!$isEdit && (
            <FComponentsLib.FTextBtn
              style={{ fontSize: 12 }}
              onClick={() => {
                set$isEdit(true);
              }}
            >
              {
                // 编辑
                FI18n.i18nNext.tAuto('btn_edit')
              }
            </FComponentsLib.FTextBtn>
          )}
          {$isEdit && (
            <>
              <FComponentsLib.FTextBtn
                style={{ fontSize: 12 }}
                type='default'
                onClick={() => {
                  set$isEdit(false);
                  initDate();
                }}
              >
                {FI18n.i18nNext.tAuto('cancel')}
              </FComponentsLib.FTextBtn>
              <FComponentsLib.FTextBtn
                style={{ fontSize: 12 }}
                onClick={async () => {
                  const { ret, errCode, msg, data } =
                    await FServiceAPI.Exhibit.setItemsAutoCollectRule({
                      presentableId: exhibitID,
                      status: get$automatic() ? 1 : 0,
                      conditionType: get$conditionType() === 'every' ? 1 : 2,
                      filterConditions: get$conditions().map((c) => {
                        return {
                          key: c.key,
                          limitOperatorType: c.limitOperatorType,
                          value: c.value,
                        };
                      }),
                    });
                  if (ret !== 0 || errCode !== 0) {
                    fMessage(msg, 'error');
                    return;
                  }
                  set$isEdit(false);
                }}
                disabled={$conditions.some((c) => {
                  return c.value === '' || c.valueError !== '';
                })}
              >
                {FI18n.i18nNext.tAuto('save')}
              </FComponentsLib.FTextBtn>
            </>
          )}
        </Space>
      </div>

      <div>
        <div style={{ height: 20 }} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              disabled={!$isEdit}
              checked={$automatic}
              style={{ margin: 0 }}
              onChange={async (e) => {
                set$automatic(e.target.checked);
              }}
            />
            <div style={{ width: 10 }} />
            <FComponentsLib.FContentText
              text={
                // '自动收入单品'
                FI18n.i18nNext.t('edit_exhibitcollection_autoadd_option')
              }
              type={'normal'}
            />
            <div style={{ width: 30 }} />
            <div className={styles.learnMore}>
              {FI18n.i18nNext.tJSXElement('edit_exhibitcollection_autoadd_hint')}
            </div>
          </div>
          {$automatic && (
            <>
              <div style={{ height: 20 }} />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Radio
                  disabled={!$isEdit}
                  style={{ margin: 0 }}
                  checked={$conditionType === 'every'}
                  onClick={async () => {
                    set$conditionType('every');
                  }}
                />
                <div style={{ width: 10 }} />
                <div style={{ color: '#222' }}>
                  {/* 满足以下所有条件 */}
                  {FI18n.i18nNext.tAuto('edit_exhibitcollection_autoadd_option_matchall')}
                </div>
                <div style={{ width: 30 }} />
                <Radio
                  disabled={!$isEdit}
                  style={{ margin: 0 }}
                  checked={$conditionType === 'some'}
                  onClick={async () => {
                    set$conditionType('some');
                  }}
                />
                <div style={{ width: 10 }} />
                <div style={{ color: '#222' }}>
                  {/* 满足以下任意条件 */}
                  {FI18n.i18nNext.tAuto('edit_exhibitcollection_autoadd_option_matchany')}
                </div>
              </div>
              <div style={{ height: 20 }} />

              <Conditions
                nodeName={nodeName}
                showDelete={$conditions.length > 1 && $isEdit}
                value={$conditions}
                onChange={(value) => {
                  set$conditions(value);
                }}
                disabled={!$isEdit}
              />

              {$isEdit && (
                <>
                  <div style={{ height: 20 }} />
                  <Space
                    size={5}
                    onClick={async () => {
                      set$conditions([
                        ...get$conditions(),
                        {
                          key: 'presentableTitle',
                          limitOperatorType: 'INCLUDES',
                          value: '',
                          valueError: '',
                        },
                      ]);
                    }}
                  >
                    <FComponentsLib.FTextBtn>
                      <FIcons.FAdd style={{ fontSize: 14 }} />
                    </FComponentsLib.FTextBtn>
                    <FComponentsLib.FTextBtn style={{ fontSize: 12 }}>
                      {/* 添加条件 */}
                      {FI18n.i18nNext.tAuto('edit_exhibitcollection_autoadd_btn_addcondition')}
                    </FComponentsLib.FTextBtn>
                  </Space>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface ConditionsProps {
  nodeName: string;
  value: AutoIncludedSettingStates['conditions'];
  showDelete?: boolean;
  disabled?: boolean;

  onChange?(value: ConditionsProps['value']): void;
}

function Conditions({
  nodeName,
  value,
  showDelete = false,
  disabled = false,
  onChange,
}: ConditionsProps) {
  const [$field_Options, set$field_Options, get$field_Options] = FUtil.Hook.useGetState<
    {
      value: 'presentableTitle' | 'authIdentity' | 'resourceTypeCode';
      label: string;
    }[]
  >([
    {
      value: 'presentableTitle',
      label:
        // '展品标题'
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_exhibittitle'),
    },
    {
      value: 'authIdentity',
      label:
        // '展品授权标识'
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_exhibitid'),
    },
    {
      value: 'resourceTypeCode',
      label:
        // '资源类型',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_resourcetype'),
    },
  ]);

  const [$term_Options, set$term_Options, get$term_Options] = FUtil.Hook.useGetState<
    {
      value: 'INCLUDES' | 'NOT_INCLUDES' | 'STARTS_WITH' | 'ENDS_WITH' | 'EQUAL' | 'NOT_EQUAL';
      label: string;
    }[]
  >([
    {
      value: 'INCLUDES',
      label:
        // '包含',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_contains'),
    },
    {
      value: 'NOT_INCLUDES',
      label:
        // '不包含',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_notcontains'),
    },
    {
      value: 'STARTS_WITH',
      label:
        // '开头包含',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_startswith'),
    },
    {
      value: 'ENDS_WITH',
      label:
        // '结尾包含',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_endswith'),
    },
    {
      value: 'EQUAL',
      label:
        // '等于',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_equalto'),
    },
    {
      value: 'NOT_EQUAL',
      label:
        // '不等于',
        FI18n.i18nNext.t('nodemgnt_autoaddtocoll_notequalto'),
    },
  ]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'stretch',
        gap: 20,
      }}
    >
      {value.map((c, ci, ca) => {
        return (
          <div key={ci}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FSelect2
                disabled={disabled}
                style={{ width: 170 }}
                value={c.key}
                options={$field_Options}
                onChange={async (v) => {
                  onChange?.(
                    value.map((condition, conditionIndex) => {
                      if (ci !== conditionIndex) {
                        return condition;
                      }
                      return {
                        ...condition,
                        key: v,
                        limitOperatorType: v === 'resourceTypeCode' ? 'EQUAL' : 'INCLUDES',
                        value: '',
                        valueError: '',
                      };
                    }),
                  );
                }}
              />
              <FSelect2
                disabled={disabled}
                style={{ width: 170 }}
                value={c.limitOperatorType}
                options={$term_Options.filter((v) => {
                  if (c.key === 'presentableTitle' || c.key === 'authIdentity') {
                    return !(v.value === 'EQUAL' || v.value === 'NOT_EQUAL');
                  }
                  return v.value === 'EQUAL';
                })}
                onChange={async (v) => {
                  onChange?.(
                    value.map((condition, conditionIndex) => {
                      if (ci !== conditionIndex) {
                        return condition;
                      }
                      return {
                        ...condition,
                        limitOperatorType: v,
                      };
                    }),
                  );
                }}
              />
              {c.key === 'presentableTitle' && (
                // <FComponentsLib.FInput.FSingleLine
                <FInput_PinyinSafeTextCounter
                  disabled={disabled}
                  style={{ width: 420 }}
                  value={c.value}
                  lengthLimit={100}
                  onChangeValue={(v) => {
                    onChange?.(
                      value.map((condition, conditionIndex) => {
                        if (ci !== conditionIndex) {
                          return condition;
                        }
                        return {
                          ...condition,
                          value: v,
                          valueError:
                            v === ''
                              ? // '不能为空'
                                FI18n.i18nNext.t('nodemgnt_autoaddtocoll_matchvalue_required', {
                                  matchfield:
                                    $field_Options.find((v) => {
                                      return v.value === c.key;
                                    })?.label || '',
                                  term:
                                    $term_Options.find((v) => {
                                      return v.value === c.limitOperatorType;
                                    })?.label || '',
                                })
                              : v.length > 100
                              ? '不能超过100个字符'
                              : '',
                        };
                      }),
                    );
                  }}
                  hasError={c.valueError !== ''}
                />
              )}

              {c.key === 'authIdentity' && (
                // <Input
                <FInput_PinyinSafeTextCounter
                  disabled={disabled}
                  style={{ width: 420, height: 38 }}
                  value={c.value}
                  lengthLimit={60}
                  prefix={
                    c.limitOperatorType === 'STARTS_WITH' ? <span>{nodeName} /</span> : undefined
                  }
                  onChangeValue={async (v) => {
                    onChange?.(
                      value.map((condition, conditionIndex) => {
                        if (ci !== conditionIndex) {
                          return condition;
                        }
                        return {
                          ...condition,
                          value: v,
                          valueError:
                            v === ''
                              ? // '不能为空'
                                FI18n.i18nNext.t('nodemgnt_autoaddtocoll_matchvalue_required', {
                                  matchfield:
                                    $field_Options.find((v) => {
                                      return v.value === c.key;
                                    })?.label || '',
                                  term:
                                    $term_Options.find((v) => {
                                      return v.value === c.limitOperatorType;
                                    })?.label || '',
                                })
                              : v.length > 60
                              ? '不能超过60个字符'
                              : '',
                        };
                      }),
                    );
                  }}
                  hasError={c.valueError !== ''}
                />
              )}

              {c.key === 'resourceTypeCode' && (
                <FResourceTypeInputBlock
                  disabled={disabled}
                  value={c.value}
                  onChange={async (v) => {
                    onChange?.(
                      value.map((condition, conditionIndex) => {
                        if (ci !== conditionIndex) {
                          return condition;
                        }
                        return {
                          ...condition,
                          value: v,
                          valueError: v === '' ? '不能为空' : '',
                        };
                      }),
                    );
                  }}
                />
              )}

              {
                // ca.length > 1 && $isEdit
                showDelete ? (
                  <FComponentsLib.FTextBtn
                    // disabled={}
                    type={'danger'}
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      onChange?.(
                        value.filter((condition, conditionIndex) => {
                          return conditionIndex !== ci;
                        }),
                      );
                      // change({
                      //   ...$setting,
                      //   conditions: ,
                      // });
                    }}
                  >
                    <FIcons.FDelete style={{ fontSize: 16 }} />
                  </FComponentsLib.FTextBtn>
                ) : (
                  <div style={{ width: 16 }} />
                )
              }
            </div>
            {c.valueError && (
              <div style={{ color: 'red', fontSize: 12, padding: '5px 0 0 410px' }}>
                {c.valueError}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
